import React, { useState, useEffect } from 'react'
import { UserService } from '@duck/api-definitions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@duck/ui'
import { UserForm } from './UserForm'
import { apis } from '@modules/apiClients'
import { useTar } from '@/modules'

interface UserDialogProps {
  isOpen: boolean
  onClose: () => void
  user: UserService.User | null
  onUserSaved?: () => void
  allAccessGroups: string[]
}

export const UserDialog: React.FC<UserDialogProps> = ({
  isOpen,
  onClose,
  user,
  onUserSaved,
  allAccessGroups
}) => {
  const { t } = useTar()
  const [formData, setFormData] = useState<Partial<UserService.User>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Reset form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({ ...user })
    } else {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'user' as UserService.UserRole,
        accessGroups: [],
        isActive: true,
        isVerified: false,
      })
    }
    setSaveError(null)
    setIsSaving(false)
  }, [user])

  const handleInputChange = (field: keyof UserService.User, value: string | boolean | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    setSaveError(null)

    // Basic validation
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setSaveError('Email, first name, and last name are required.')
      return
    }

    if (!formData.accessGroups || formData.accessGroups.length === 0) {
      setSaveError('At least one access group is required.')
      return
    }

    if (!formData.role) {
      setSaveError('Role is required.')
      return
    }

    try {
      setIsSaving(true)
      if (user) {
        // Update existing user
        const updateData: UserService.UpdateUserRequest = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role as UserService.UserRole,
          isActive: formData.isActive as boolean,
          isVerified: formData.isVerified as boolean,
          accessGroups: formData.accessGroups as string[],
          notes: formData.notes as string,
        }
        await apis.userService.idPut(user.id, updateData)
      } else {
        // Create new user - Note: This might not be available in the API
        // For now, we'll show an error message
        setSaveError('Creating new users directly is not supported. Please use invitations instead.')
        return
      }

      // Call the callback to refresh parent component
      onUserSaved?.()
      onClose()
    } catch (error: unknown) {
      console.error('Failed to save user:', error)
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Failed to save user. Please try again.'
      setSaveError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const isReadOnly = false // Users can always be edited

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !isSaving && !open && onClose()}
    >
      <DialogContent
        className="sm:max-w-3xl lg:max-w-4xl sm:min-w-[500px] lg:min-w-[600px] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => isSaving && e.preventDefault()}
        onEscapeKeyDown={(e) => isSaving && e.preventDefault()}
        onInteractOutside={(e) => isSaving && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {user ? `${t('users.editUser')}: ${user.email}` : t('users.addUser')}
          </DialogTitle>
        </DialogHeader>

        <UserForm
          formData={formData}
          onChange={handleInputChange}
          onSave={handleSave}
          onCancel={onClose}
          isSaving={isSaving}
          saveError={saveError}
          isEditing={!!user}
          isReadOnly={isReadOnly}
          allAccessGroups={allAccessGroups}
        />
      </DialogContent>
    </Dialog>
  )
}
